import { EditorView } from '@codemirror/view';

/**
 * The manuscript: a paper page in the text face (proportional sans, tabular figures), generous
 * margins, row labels as soft serif headings. No IDE chrome: line numbers are opt-in.
 */
export const editorTheme = EditorView.theme({
  '&': {
    height: '100%',
    fontSize: '15px',
    color: 'rgb(var(--c-fg))',
    backgroundColor: 'rgb(var(--c-editor))',
  },
  '&.cm-focused': { outline: 'none' },
  '.cm-scroller': {
    fontFamily: 'var(--font-sans)',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: '1.75',
  },
  '.cm-content': { caretColor: 'rgb(var(--c-accent))', padding: '24px 0 64px', maxWidth: 'var(--measure)' },
  '.cm-line': { padding: '0 32px 0 16px' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'rgb(var(--c-accent))', borderLeftWidth: '2px' },
  '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, ::selection': {
    backgroundColor: 'var(--editor-selection)',
  },
  '.cm-gutters': {
    backgroundColor: 'rgb(var(--c-editor))',
    color: 'rgb(var(--c-fg-subtle))',
    border: 'none',
    paddingLeft: '12px',
  },
  '.cm-lineNumbers .cm-gutterElement': {
    padding: '0 4px 0 8px',
    minWidth: '32px',
    fontSize: '11px',
    fontVariantNumeric: 'tabular-nums',
  },
  '.cm-activeLine': { backgroundColor: 'var(--editor-active-line)' },
  '.cm-activeLineGutter': { backgroundColor: 'transparent', color: 'rgb(var(--c-fg))' },
  '.cm-placeholder': { color: 'rgb(var(--c-fg-subtle))', fontStyle: 'italic' },

  '.cm-fl-diag-gutter .cm-gutterElement': {
    width: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '.cm-fl-marker': { display: 'inline-flex', cursor: 'default' },
  '.cm-fl-marker-error': { color: 'rgb(var(--c-error))' },
  '.cm-fl-marker-warning': { color: 'rgb(var(--c-warning))' },
  '.cm-fl-marker-info': { color: 'rgb(var(--c-info))' },
  '.cm-fl-line-error': { backgroundColor: 'rgb(var(--c-error) / 0.07)' },
  '.cm-fl-line-warning': { backgroundColor: 'rgb(var(--c-warning) / 0.07)' },
  '.cm-fl-line-info': { backgroundColor: 'rgb(var(--c-info) / 0.04)' },

  // Reading tone (ADR-013, decision 6), as a thin rule in the paper margin (a background image, so it
  // stacks with the diagnostic tint); each tone has its own shape, not only its colour: solid lichen for a
  // verified row, dotted honey for a row executed on trust, dashed honey for a row read by island and not
  // confirmed, dash-dot amber for a row not read but with proposals, hollow grey for a row not read.
  '.cm-row-checked, .cm-row-unverified, .cm-row-island, .cm-row-proposals, .cm-row-unread': {
    backgroundSize: '3px 100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '5px 0',
  },
  '.cm-row-checked': {
    backgroundImage: 'linear-gradient(rgb(var(--c-success) / 0.5), rgb(var(--c-success) / 0.5))',
  },
  '.cm-row-unverified': {
    backgroundImage: 'repeating-linear-gradient(to bottom, rgb(var(--c-warning) / 0.75) 0 3px, transparent 3px 6px)',
  },
  '.cm-row-island': {
    backgroundImage: 'repeating-linear-gradient(to bottom, rgb(var(--c-warning) / 0.75) 0 8px, transparent 8px 11px)',
  },
  '.cm-row-proposals': {
    backgroundImage:
      'repeating-linear-gradient(to bottom, rgb(var(--c-warning) / 0.9) 0 7px, transparent 7px 9px, rgb(var(--c-warning) / 0.9) 9px 11px, transparent 11px 13px)',
  },
  '.cm-row-unread': {
    backgroundImage:
      'linear-gradient(to right, rgb(var(--c-fg-subtle) / 0.55) 0 1px, transparent 1px 2px, rgb(var(--c-fg-subtle) / 0.55) 2px 3px)',
  },

  '.cm-tooltip': {
    border: '1px solid rgb(var(--c-border))',
    backgroundColor: 'rgb(var(--c-surface))',
    color: 'rgb(var(--c-fg))',
    borderRadius: 'var(--radius-card)',
    boxShadow: 'var(--shadow-md)',
  },
  '.cm-fl-tooltip': { padding: '12px 14px', maxWidth: '440px', fontFamily: 'var(--font-sans)', fontSize: '13px', lineHeight: '1.5' },
  '.cm-fl-tip + .cm-fl-tip': { borderTop: '1px solid rgb(var(--c-border))', marginTop: '10px', paddingTop: '10px' },
  '.cm-fl-tip-head': { display: 'flex', alignItems: 'center', gap: '8px' },
  '.cm-fl-tip-head strong': { fontFamily: 'var(--font-serif)', fontWeight: '500', fontSize: '15px' },
  '.cm-fl-tip-icon': { display: 'inline-flex' },
  '.cm-fl-tip-sizes': {
    marginLeft: 'auto',
    fontSize: '11px',
    fontWeight: '700',
    color: 'rgb(var(--c-fg))',
    backgroundColor: 'rgb(var(--c-heather) / 0.14)',
    borderRadius: '999px',
    padding: '0 8px',
  },
  '.cm-fl-tip-explain': { marginTop: '4px', fontSize: '12px', color: 'rgb(var(--c-fg-muted))' },
  '.cm-fl-tip-msg': { marginTop: '4px', color: 'rgb(var(--c-fg))' },
  '.cm-fl-tip-status': { display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '12px', color: 'rgb(var(--c-fg-muted))' },
  '.cm-fl-tip-glyph': { fontWeight: '700', minWidth: '10px', textAlign: 'center' },
  '.cm-row-tip-checked .cm-fl-tip-glyph': { color: 'rgb(var(--c-success))' },
  '.cm-row-tip-unverified .cm-fl-tip-glyph, .cm-row-tip-island .cm-fl-tip-glyph, .cm-row-tip-proposals .cm-fl-tip-glyph': {
    color: 'rgb(var(--c-warning))',
  },
  '.cm-row-tip-unread .cm-fl-tip-glyph': { color: 'rgb(var(--c-fg-subtle))' },
});
